import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./LoginPage.css";
import "./Dashboard.css";
import { Button, FormInput } from "../components/shared";
import ThirdParty from "supertokens-auth-react/recipe/thirdparty";
import { useSupertokens } from "../hooks";
import JointPhotosIconFixedColors from "../joint-photos-icon-fixed-colors.svg";

interface LoginPageProps {
  onLoginSuccess?: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const { isLoading, message, isCodeSent, sendCode, verifyCode, resetState } = useSupertokens();

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendCode(email);
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await verifyCode(code);
    if (success && onLoginSuccess) {
      setTimeout(() => {
        onLoginSuccess();
      }, 2000);
    }
  };

  const handleFacebookLogin = async () => {
    await ThirdParty.redirectToThirdPartyLogin({
      thirdPartyId: "facebook",
    });
  };

  const handleGoogleLogin = async () => {
    await ThirdParty.redirectToThirdPartyLogin({
      thirdPartyId: "google",
    });
  };

  return (
    <div
      data-section="login-page-root"
      className="jp-page min-h-screen relative overflow-hidden flex items-center justify-center px-4 py-10"
    >
      <div className="login-container">
        <div className="login-header">
          <div className="login-title-row">
            <h1 className="app-title">
              <span style={{ color: "var(--primary-green)" }}>joint.</span>
              <span style={{ color: "var(--primary-blue)" }}>photos</span>
            </h1>
            <img
              className="login-brand-icon"
              src={JointPhotosIconFixedColors}
              alt="joint.photos"
              width={44}
              height={44}
              draggable={false}
            />
          </div>
          <p className="app-subtitle">Share your moments, together</p>
        </div>

        <div className="login-form-container">
          {!isCodeSent ? (
            <form onSubmit={handleSendCode} className="login-form">
              <FormInput
                type="email"
                label="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                disabled={isLoading}
              />

              <Button type="submit" disabled={isLoading || !email} isLoading={isLoading} loadingText="Sending...">
                Send Magic Link & Code
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyCode} className="login-form">
              <FormInput
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                label="Enter Code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="Enter the code from your email"
                required
                disabled={isLoading}
              />

              <Button type="submit" disabled={isLoading || !code} isLoading={isLoading} loadingText="Verifying...">
                Verify Code
              </Button>

              <Button type="button" variant="secondary" onClick={resetState} disabled={isLoading}>
                Back to Email
              </Button>
            </form>
          )}

          {message && <div className={`message ${message.includes("error") ? "error" : "success"}`}>{message}</div>}
        </div>

        <div className="login-footer">
          <div className="divider">
            <span>or continue with</span>
          </div>

          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={isLoading}
            className="social-button google-button"
          >
            <svg className="social-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <span>Continue with Google</span>
          </button>

          <button
            type="button"
            onClick={handleFacebookLogin}
            disabled={isLoading}
            className="social-button facebook-button"
          >
            <svg className="social-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="#1877F2"/>
            </svg>
            <span>Continue with Facebook</span>
          </button>

          <div className="mt-5 flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm text-slate-200">
            <Link className="underline hover:text-white" to="/privacy">
              Privacy
            </Link>
            <Link className="underline hover:text-white" to="/data-deletion">
              Data deletion
            </Link>
          </div>

          {/* <p className="footer-text">
            Secure authentication powered by <span style={{ color: "#a78bfa" }}>SuperTokens</span>
          </p> */}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
